import {
  DataTable,
  LeveledFiltersConfig,
} from "@/components/data-table/data-table";
import {
  getLegislationColumns,
  buildDynamicColumns,
  DynamicFilterDef,
} from "./Columns";
import { useEffect, useMemo, useRef, useState } from "react";
import { LegislationTableRowSkeleton } from "./LegislationTableRowSkeleton";
import useConsolidationPrioritizationEnabled from "@/hooks/useConsolidationPrioritizationEnabled";
import PrioritizeLawDialog from "@/pages/settings/consolidation-priority/PrioritizeLawDialog";
import type { SelectedLegislation } from "@/pages/dashboard/config/LegislationPickerDialog";
import { useLegislationTableStore } from "@/stores/legislation-table-state.store";
import useLegislation from "@/hooks/useLegislation";
import { useNavigate } from "react-router-dom";
import { openAppNavigation } from "@/lib/navigation/appNavigation";
import { debounce } from "lodash";
import {
  SourceCfg,
  SourceDictionary,
} from "@/models/legislation/LegislationFilter";
import useUsers from "@/hooks/useUsers";
import useGroups from "@/hooks/useGroups";
import { useLegislationFilterConfig } from "@/providers/legislation-filter/hooks/useLegislationFilterConfig";
import { useLegislationTableFilterState } from "@/providers/legislation-filter/hooks/useLegislationTableFilterState";
import { DataSource, normalizeDataSource } from "@/config/datasources";
import { Subscription } from "@/models/subscriptions/Subscription";
import useUserTokenStore from "@/stores/user-token.store";
import { Permission } from "@/lib/permissions/Permission.enum";
import { permissionMap } from "@/lib/permissions/permissionMap";
import useConsolidationRenderingEnabled from "@/hooks/useConsolidationRenderingEnabled";
import { resolveLegislationOpenNavigationTarget } from "@/lib/consolidationLegislationUtils";
import { useSaveSourceSearchOnLeave } from "@/hooks/useSaveSourceSearchOnLeave";
import {
  toSourceSearch,
  type SourceSearchTableState,
} from "@/lib/search/sourceSearch";
import CopyLinkButton from "@/pages/global-search/components/search-input/CopyLinkButton";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { logger } from "@/lib/logger";

/**
 * Additional column definitions by datasource.
 */
const ADDITIONAL_COLUMNS: Partial<Record<DataSource, DynamicFilterDef[]>> = {
  [DataSource.Domsdatabasen]: [
    {
      key: "court_case_number",
      displayName: "Rettens sagsnummer",
      type: "Single",
      databaseColumn: "metadata_object->'court_case_number'",
      valueMapping: null,
    },
  ],
  [DataSource.Eurlex]: [
    {
      key: "CelexNumber",
      displayName: "Celex nummer",
      type: "Single",
      databaseColumn: "metadata_object->'CelexNumber'",
      valueMapping: null,
    },
  ],
  [DataSource.Arvid]: [
    {
      key: "journal_number",
      displayName: "Journal nummer",
      type: "Single",
      databaseColumn: "metadata_object->'journal_number'",
      valueMapping: null,
    },
  ],
};
import {
  parseSearchTags,
  reorderColumnsById,
  sortFilterDefsBySourceOrder,
  SOURCE_COLUMN_ORDERS,
  SOURCE_DYNAMIC_FILTER_ORDERS,
} from "../utils/tableUtils";
import {
  areDefaultColumnFiltersApplied,
  buildDefaultColumnFilters,
  withSourceDefaultFilters,
} from "../utils/defaultFilters";
import { getSelectedDatasource } from "../utils/datasourceFilter";

/**
 * `processedFilters` re-runs on every filter change, so a config filter without
 * options is reported once per page load, not once per change.
 */
const reportedOptionlessFilters = new Set<string>();

const LegislationTable = () => {
  const { useGetPaginated } = useLegislation();
  const navigate = useNavigate();
  const {
    data,
    search,
    searchTags,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    columnFilters,
    setTableState,
    navigateBack,
    setNavigateBack,
  } = useLegislationTableStore();
  const { useQueryUsersFavoriteDocuments, useQuerySubscriptions } = useUsers();
  const { useMyGroupsLegislationSubscriptionKeys } = useGroups();
  const fetchedFavoriteDocuments = useQueryUsersFavoriteDocuments();
  const fetchedSubscriptions = useQuerySubscriptions();
  const {
    groupLegislationSubscriptionKeys,
    isLoading: isGroupSubscriptionsLoading,
  } = useMyGroupsLegislationSubscriptionKeys();
  const hasCreateFeatureGroups = useUserTokenStore((s) =>
    s.permissions.includes(permissionMap[Permission.CreateFeatureGroups])
  );
  const { enabled: consolidationFeatureEnabled } =
    useConsolidationRenderingEnabled();
  const prioritizationFlagEnabled = useConsolidationPrioritizationEnabled();
  const hasManageConsolidationPriority = useUserTokenStore((s) =>
    s.permissions.includes(permissionMap[Permission.ManageConsolidationPriority])
  );
  const canPrioritizeConsolidation =
    prioritizationFlagEnabled && hasManageConsolidationPriority;
  const [prioritizeLaw, setPrioritizeLaw] =
    useState<SelectedLegislation | null>(null);

  // Get filter config and defaults from context
  const {
    filterConfig,
    defaultsPlan,
    isLoading: isFilterConfigLoading,
  } = useLegislationFilterConfig();
  const {
    initializeDefaults,
    markDefaultsInitialized,
    hasInitializedDefaults,
  } = useLegislationTableFilterState();

  // State
  const [favoriteLegislationIds, setFavoriteLegislationIds] = useState<
    string[]
  >([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [searchValue, setSearchValue] = useState(search);
  const [searchTagsValue, setSearchTagsValue] = useState(searchTags);
  const hasAppliedDefaultsRef = useRef(false);

  // What the user typed, not the debounced store value — leaving the page
  // inside the debounce window would otherwise store a prefix of the search.
  // A source search reaches recent searches on leaving the page (LAR-422).
  const sourceSearchState = useMemo<SourceSearchTableState>(
    () => ({
      search: searchValue,
      searchTags: searchTagsValue,
      columnFilters,
    }),
    [searchValue, searchTagsValue, columnFilters]
  );
  useSaveSourceSearchOnLeave(sourceSearchState);

  const handleAddSearchTag = (searchTag: string) => {
    const tags = parseSearchTags(searchTag);
    setSearchTagsValue((prev) => [...prev, ...tags]);
    setSearchValue("");
    setTableState({
      searchTags: [...searchTagsValue, ...tags],
      search: "",
    });
  };

  const handleRemoveSearchTag = (index: number) => {
    setSearchTagsValue((prev) => prev.filter((_, i) => i !== index));
    setTableState({
      searchTags: searchTagsValue.filter((_, i) => i !== index),
    });
  };

  const handleClearSearchTags = () => {
    setSearchTagsValue([]);
    setTableState({
      searchTags: [],
    });
  };

  // Get additional column definitions for a specific datasource
  const getAdditionalColumnDefs = (datasource: string): DynamicFilterDef[] => {
    const normalized = normalizeDataSource(datasource);
    if (!normalized) return [];
    return ADDITIONAL_COLUMNS[normalized] ?? [];
  };

  // Filters
  const selectedDatasource = useMemo(
    () => getSelectedDatasource(columnFilters ?? []),
    [columnFilters]
  );

  const showConsolidationProgressColumn =
    consolidationFeatureEnabled &&
    normalizeDataSource(selectedDatasource) === DataSource.Retsinformation;

  // Process filters based on accessToAllFilter setting
  const processedFilters = useMemo(() => {
    const baseFilters =
      columnFilters?.filter((f) => f.id !== "datasource") ?? [];

    // If no datasource is selected or no filter config, return filters as-is
    if (!selectedDatasource || !filterConfig?.sources?.[selectedDatasource]) {
      return baseFilters;
    }

    const sourceCfg = filterConfig.sources[selectedDatasource] as SourceCfg;

    if (sourceCfg.accessToAllFilter === true) {
      return baseFilters;
    }

    const processed: typeof baseFilters = [];
    const existingFilterIds = new Set(baseFilters.map((f) => f.id));

    // Process existing filters
    for (const filter of baseFilters) {
      const configFilter = sourceCfg.filters?.[filter.id];

      // If this filter exists in config and has empty selection (0 options selected)
      if (
        configFilter &&
        Array.isArray(filter.value) &&
        filter.value.length === 0
      ) {
        // Replace with all options from configuration
        const allOptions = configFilter.options || [];
        if (allOptions.length > 0) {
          // Map options to proper format (handle both string and object options)
          const optionValues = allOptions.map((opt) => {
            if (typeof opt === "string") return opt;
            if (opt && typeof opt === "object" && "value" in opt) {
              return (opt as { value: unknown }).value;
            }
            return opt;
          });
          processed.push({ ...filter, value: optionValues });
          continue;
        }
      }

      // Otherwise, keep the filter as-is
      processed.push(filter);
    }

    // Add filters from config that don't exist in columnFilters (with all options)
    for (const [filterKey, configFilter] of Object.entries(
      sourceCfg.filters || {}
    )) {
      if (!existingFilterIds.has(filterKey)) {
        // This childFilter was not selected by user - add it with all options
        const allOptions = configFilter.options || [];

        if (allOptions.length > 0) {
          // Map options to proper format (handle both string and object options)
          const optionValues = allOptions.map((opt) => {
            if (typeof opt === "string") return opt;
            if (opt && typeof opt === "object" && "value" in opt) {
              return (opt as { value: unknown }).value;
            }
            if (
              opt &&
              typeof opt === "object" &&
              "label" in opt &&
              "value" in opt
            ) {
              return (opt as { value: unknown }).value;
            }
            return opt;
          });
          processed.push({ id: filterKey, value: optionValues });
        } else if (
          !reportedOptionlessFilters.has(`${selectedDatasource}:${filterKey}`)
        ) {
          reportedOptionlessFilters.add(`${selectedDatasource}:${filterKey}`);
          logger.warn("Filter has no options in the source filter config", {
            operation: "processLegislationFilters",
            filterKey,
            datasource: selectedDatasource,
          });
        }
      }
    }

    return processed;
  }, [columnFilters, selectedDatasource, filterConfig]);

  // Map sortBy from accessorKey (f.key) to databaseColumn for backend sorting
  const sortByMapped = useMemo(() => {
    if (!sorting.length || !sorting[0].id) return "";

    const sortId = sorting[0].id;

    // The consolidation column is computed client-side from row metadata and has
    // no database column; sending its id as sortBy makes the backend query fail.
    // Guards against a stale sort persisted in sessionStorage before the column
    // became non-sortable.
    if (sortId === "consolidationProgress") return "";

    // Check if it's a dynamic column by looking up in filter config
    if (filterConfig && selectedDatasource) {
      const sourceCfg = filterConfig.sources?.[selectedDatasource];
      const filterDef = sourceCfg?.filters?.[sortId];
      if (filterDef?.databaseColumn) {
        return filterDef.databaseColumn;
      }
    }

    // Check additionalCols (they have databaseColumn set directly)
    if (selectedDatasource) {
      const additionalColumnDefs = getAdditionalColumnDefs(selectedDatasource);
      const additionalCol = additionalColumnDefs.find(
        (col) => col.key === sortId
      );
      if (additionalCol?.databaseColumn) {
        return additionalCol.databaseColumn;
      }
    }

    // Check static columns' meta for databaseColumn
    const staticColumns = getLegislationColumns(favoriteLegislationIds, []);
    const staticCol = staticColumns.find(
      (col) => "accessorKey" in col && col.accessorKey === sortId
    );
    if (staticCol?.meta?.databaseColumn) {
      return staticCol.meta.databaseColumn;
    }

    // For static columns or if no databaseColumn found, use the original id
    return sortId;
  }, [sorting, filterConfig, selectedDatasource, favoriteLegislationIds]);

  const fetchedLegislations = useGetPaginated(
    {
      pageNumber: pageIndex + 1,
      pageSize,
      sortAscending: sorting.length ? !sorting[0].desc : false,
      sortBy: sortByMapped,
      search: (search + " " + searchTagsValue.join(" ")).trim(),
      filters: processedFilters,
    },
    selectedDatasource ? [selectedDatasource] : undefined,
    {
      onPageMismatch: (_requestedPage, actualPage) => {
        setTableState({ pageIndex: actualPage - 1 });
      },
    }
  );

  const leveledFilters = useMemo<LeveledFiltersConfig | undefined>(() => {
    if (!filterConfig) return;
    const sources = (filterConfig as SourceDictionary).sources ?? filterConfig;
    return { rootColumnId: "datasource", levels: sources };
  }, [filterConfig]);

  const dynamicCols = useMemo(() => {
    if (!leveledFilters || !selectedDatasource) return [];
    const levelCfg = leveledFilters.levels?.[selectedDatasource] as
      | SourceCfg
      | undefined;
    if (!levelCfg) return [];
    const defs = Object.entries(levelCfg.filters || {})
      .filter(([objectKey]) => objectKey !== "status") // Exclude status filter since we have a dedicated Status column
      .map(([objectKey, f]) => ({
        key: objectKey,
        displayName: f.displayName ?? objectKey,
        type: f.type,
        databaseColumn: f.databaseColumn ?? objectKey,
        valueMapping: f.valueMapping ?? null,
      }));

    const normalizedSource = normalizeDataSource(selectedDatasource);
    const dynamicFilterOrder = normalizedSource
      ? SOURCE_DYNAMIC_FILTER_ORDERS[normalizedSource]
      : undefined;
    const sortedDefs = sortFilterDefsBySourceOrder(defs, dynamicFilterOrder);

    return buildDynamicColumns(sortedDefs);
  }, [leveledFilters, selectedDatasource]);

  // Build additional columns based on selectedDatasource (not related to filters)
  const additionalCols = useMemo(() => {
    if (!selectedDatasource) return [];

    const additionalColumnDefs = getAdditionalColumnDefs(selectedDatasource);
    return buildDynamicColumns(additionalColumnDefs);
  }, [selectedDatasource]);

  useEffect(() => {
    if (fetchedFavoriteDocuments.isFetched && fetchedFavoriteDocuments.data) {
      setFavoriteLegislationIds(
        fetchedFavoriteDocuments.data.favoriteLegislations.map(
          (fav) => fav.externalId
        )
      );
    }
  }, [fetchedFavoriteDocuments.isFetched, fetchedFavoriteDocuments.data]);

  useEffect(() => {
    if (fetchedSubscriptions.isFetched && fetchedSubscriptions.data) {
      setSubscriptions(fetchedSubscriptions.data);
    }
  }, [fetchedSubscriptions.isFetched, fetchedSubscriptions.data]);

  useEffect(() => {
    if (
      fetchedLegislations.isFetched &&
      fetchedLegislations.data &&
      fetchedLegislations.data.data
    ) {
      setTableState({
        data: fetchedLegislations.data.data,
        pageCount: fetchedLegislations.data.totalPages,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedLegislations.data, fetchedLegislations.isFetched]);

  // Initialize defaults only once when component mounts and no filters exist
  useEffect(() => {
    if (
      isFilterConfigLoading ||
      !defaultsPlan ||
      hasAppliedDefaultsRef.current
    ) {
      return;
    }

    // Check if user already has selections
    const hasDatasource = columnFilters.some((f) => f.id === "datasource");

    // If user has selections, mark as initialized (they've made their choice)
    if (hasDatasource) {
      if (!hasInitializedDefaults) {
        markDefaultsInitialized();
      }
      hasAppliedDefaultsRef.current = true;
      return;
    }

    const initializedFilters = initializeDefaults(columnFilters, defaultsPlan);

    if (JSON.stringify(initializedFilters) !== JSON.stringify(columnFilters)) {
      setTableState({ columnFilters: initializedFilters });
      markDefaultsInitialized();
      hasAppliedDefaultsRef.current = true;
    } else {
      hasAppliedDefaultsRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    defaultsPlan,
    isFilterConfigLoading,
    hasInitializedDefaults,
    initializeDefaults,
    markDefaultsInitialized,
    columnFilters,
  ]);

  // Ensure we mark as initialized when tenantId becomes available if we already applied defaults
  useEffect(() => {
    if (hasAppliedDefaultsRef.current && !hasInitializedDefaults) {
      markDefaultsInitialized();
    }
  }, [hasInitializedDefaults, markDefaultsInitialized]);

  // Choosing a source asks for that source's predefined filters, the way the
  // same choice does in global search. A return to the list must not: the table
  // remounts with the source already selected, which is why this compares
  // against the source the mount started with rather than to `undefined`.
  const previousDatasourceRef = useRef(selectedDatasource);

  useEffect(() => {
    if (previousDatasourceRef.current === selectedDatasource) {
      return;
    }
    previousDatasourceRef.current = selectedDatasource;

    if (!defaultsPlan || selectedDatasource !== defaultsPlan.sourceKey) {
      return;
    }

    // The toolbar drops the previous source's filters in its own effect, which
    // runs before this one, so build on the store's filters and not on this
    // render's.
    const currentFilters = useLegislationTableStore.getState().columnFilters;
    const withDefaults = withSourceDefaultFilters(currentFilters, defaultsPlan);

    if (withDefaults !== currentFilters) {
      setTableState({ columnFilters: withDefaults });
    }
  }, [selectedDatasource, defaultsPlan, setTableState]);

  const handleRowClick = (rowIndex: number, event?: React.MouseEvent) => {
    const legislation = data[rowIndex];

    openAppNavigation({
      target: resolveLegislationOpenNavigationTarget(
        legislation,
        consolidationFeatureEnabled
      ),
      navigate,
      event,
    });
  };

  // The tenant's predefined filters are only ever applied on the first visit of
  // a session, so clearing the filters (or coming back later) leaves the user
  // with no way back to them. This action is that way back, and is offered
  // whenever the tenant has predefined filters that are not already in effect.
  const canApplyDefaultFilters =
    defaultsPlan !== null &&
    !areDefaultColumnFiltersApplied(columnFilters, defaultsPlan);

  const handleApplyDefaultFilters = () => {
    setTableState({ columnFilters: buildDefaultColumnFilters(defaultsPlan) });
  };

  const handleRootSelectionChange = () => {
    if (navigateBack) {
      setNavigateBack(false);
    } else {
      // Reset pageIndex and sorting when root selection changes
      setTableState({
        pageIndex: 0,
        sorting: [],
      });
    }
  };

  // Track previous non-root filters to detect changes
  const previousNonRootFiltersRef = useRef<string>(
    JSON.stringify((columnFilters ?? []).filter((f) => f.id !== "datasource"))
  );

  // Reset pageIndex when non-root filters change
  useEffect(() => {
    const nonRootFilters = (columnFilters ?? []).filter(
      (f) => f.id !== "datasource"
    );
    const currentNonRootFiltersString = JSON.stringify(nonRootFilters);
    const previousNonRootFiltersString = previousNonRootFiltersRef.current;

    // Only reset if non-root filters actually changed
    if (currentNonRootFiltersString !== previousNonRootFiltersString) {
      // Update the ref first to prevent infinite loops
      previousNonRootFiltersRef.current = currentNonRootFiltersString;

      // Reset pageIndex when non-root filters change
      setTableState({
        pageIndex: 0,
      });
    }
  }, [columnFilters, setTableState]);

  // Debounced function to update the store state
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setTableState({
          // Reset pageIndex only if the search value has changed
          pageIndex: value !== search ? 0 : pageIndex,
          search: value,
        });
      }, 300),
    [setTableState, search, pageIndex]
  );

  // Synchronize searchValue with the store
  useEffect(() => {
    debouncedSetSearch(searchValue);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchValue, debouncedSetSearch]);

  const columns = useMemo(() => {
    const baseColumns = getLegislationColumns(
      favoriteLegislationIds,
      [...dynamicCols, ...additionalCols],
      subscriptions,
      groupLegislationSubscriptionKeys,
      hasCreateFeatureGroups,
      showConsolidationProgressColumn,
      canPrioritizeConsolidation
        ? (item) =>
            setPrioritizeLaw({
              externalId: item.externalId,
              datasource: item.datasource,
              title: item.title,
            })
        : undefined
    );

    const normalizedSource = normalizeDataSource(selectedDatasource);
    const columnOrder = normalizedSource
      ? SOURCE_COLUMN_ORDERS[normalizedSource]
      : undefined;

    return columnOrder
      ? reorderColumnsById(baseColumns, columnOrder)
      : baseColumns;
  }, [
    favoriteLegislationIds,
    dynamicCols,
    additionalCols,
    subscriptions,
    groupLegislationSubscriptionKeys,
    hasCreateFeatureGroups,
    showConsolidationProgressColumn,
    canPrioritizeConsolidation,
    selectedDatasource,
  ]);

  // `status` exists only so the status filter has a column to attach to: it
  // renders no header and no cell, but left visible it still costs a full
  // padded, empty column at the end of every row.
  const defaultColumnVisibility = useMemo(
    () => ({ datasource: !selectedDatasource, status: false }),
    [selectedDatasource]
  );

  return (
    <>
      <DataTable
      columns={columns}
      data={data}
      pageCount={pageCount}
      pageIndex={pageIndex}
      pageSize={pageSize}
      setPageIndex={(pageIndex: number) => setTableState({ pageIndex })}
      setPageSize={(pageSize: number) => setTableState({ pageSize })}
      sorting={sorting}
      setSorting={(sorting) => setTableState({ sorting })}
      handleSearchChange={setSearchValue}
      search={searchValue}
      searchTags={searchTagsValue}
      handleAddSearchTag={handleAddSearchTag}
      handleRemoveSearchTag={handleRemoveSearchTag}
      handleClearSearchTags={handleClearSearchTags}
      handleRowClick={handleRowClick}
      columnFilters={columnFilters}
      setColumnFilters={(columnFilters) => setTableState({ columnFilters })}
      isLoading={
        fetchedLegislations.isPending ||
        isFilterConfigLoading ||
        fetchedSubscriptions.isLoading ||
        isGroupSubscriptionsLoading ||
        fetchedLegislations.isPlaceholderData
      }
      skeletonComponent={LegislationTableRowSkeleton}
      stickyHeader={true}
      alwaysShowFilters={true}
      shouldDisplayHoverContent={true}
      leveledFilters={leveledFilters}
      onRootSelectionChange={handleRootSelectionChange}
      onApplyDefaultFilters={
        canApplyDefaultFilters ? handleApplyDefaultFilters : undefined
      }
      showSearchSyntaxHelp={true}
      searchActions={
        <PermissionGate allOf={[Permission.CreateSearchShare]}>
          <CopyLinkButton
            searchQuery={toSourceSearch(sourceSearchState)}
            buttonClassName="size-8 group hover:bg-muted"
          />
        </PermissionGate>
      }
      defaultColumnVisibility={defaultColumnVisibility}
      />
      <PrioritizeLawDialog
        open={prioritizeLaw !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPrioritizeLaw(null);
        }}
        initialLaw={prioritizeLaw}
      />
    </>
  );
};

export default LegislationTable;
